/**
 * Client-side mirror of backend/utils/landingPageSchema.js's fixed
 * component allowlist. This registry adds the builder-only metadata
 * (display name/icon/category/default props/prop-editor fields) that the
 * backend schema doesn't need — the backend remains the actual security
 * boundary (see registry/validate.js's cross-reference comment); this
 * file is purely about what the palette/canvas/inspector show.
 *
 * Deliberately NOT user-extensible — see the plan's "Deferred" section:
 * no generic plugin/widget system. Adding a new component type means
 * editing this file AND backend/utils/landingPageSchema.js's ALL_TYPES,
 * by design (a closed allowlist, not an open registry).
 */

import { COLOR_TOKENS, RADIUS_TOKENS } from '../tokens';

let idCounter = 0;
export function generateNodeId() {
    idCounter += 1;
    return `n${Date.now().toString(36)}${idCounter}`;
}

export const CATEGORIES = ['layout', 'content', 'hotwifi'];

// The look of a login form. The forms themselves are the existing,
// unmodified components (only their markup is fixed), so these are
// presentational props the guest renderer turns into CSS scoped to the
// node — see buildLoginThemeCss in Portal/landingPageStyles.js. Left on
// "Default", a login form looks exactly as it always has.
// A container set to "free" lets you place, resize and rotate everything inside it
// anywhere with the mouse (each thing is stored as a % of the container).
// free = draw anywhere, and it re-flows on phones/tablets by itself; free-manual = draw anywhere,
// and you set each device yourself.
const LAYOUT_FIELD = { key: 'layout', label: 'Layout (flow = stacked, free = place anywhere & adapts to every device automatically, free-manual = you set each device)', type: 'select', options: ['flow', 'free', 'free-manual'] };
const colorField = (key, label) => ({ key, label, type: 'token', tokens: COLOR_TOKENS });
const FORM_LOOK_FIELDS = [
    colorField('labelColor', 'Labels & checkbox text colour'),
    colorField('inputBg', 'Field background'),
    colorField('inputColor', 'Field text colour'),
    colorField('inputBorder', 'Field border colour'),
    { key: 'inputRadius', label: 'Field corner radius', type: 'token', tokens: RADIUS_TOKENS },
    colorField('linkColor', 'Link colour'),
];
const TAB_LOOK_FIELDS = [
    colorField('tabColor', 'Tab text colour'),
    colorField('tabBg', 'Tab background'),
    colorField('tabActiveColor', 'Active tab colour'),
];
// One whole look for the login box: 'boutique' = segmented tabs, plain fields with
// the label above, brand-colour Connect button (Landing.css, .lp-look-boutique).
// Every login method takes the same look, so a page looks the same whichever the
// branch enables. The colour fields above still apply on top of it.
const LOOK_FIELD = { key: 'look', label: 'Style (default = classic, boutique = segmented tabs and clean fields in the brand colour)', type: 'select', options: ['default', 'boutique'] };
// Where the "You're connected" / "Can't connect" screens appear after sign-in
// (in place of this box's content). Without one, the guest sees a short message instead.
const STATUS_PANEL_FIELD = { key: 'statusPanel', label: 'Show the connected / can not connect screens in this box after sign-in', type: 'select', options: ['no', 'yes'] };
// For a box whose own content brings its padding (a card of banner + panel): space
// around those screens, which have none of their own.
const STATUS_INSET_FIELD = { key: 'statusInset', label: 'Space around those screens (yes = this box has no padding of its own)', type: 'select', options: ['no', 'yes'] };
// Wording for the room-number form (blank = the form's own).
const PMS_HINT_FIELDS = [
    { key: 'roomPlaceholder', label: 'Room number placeholder', type: 'text' },
    { key: 'lastNameHint', label: 'Hint under the last name field', type: 'text' },
];
// Tab names (blank = the standard name). A name set for the branch under Login
// Policy still wins, since that is where the property itself manages them.
const TAB_LABEL_FIELDS = [
    { key: 'pmsTabLabel', label: 'Room guest tab name', type: 'text' },
    { key: 'voucherTabLabel', label: 'Voucher / access code tab name', type: 'text' },
    { key: 'otpTabLabel', label: 'OTP tab name', type: 'text' },
    { key: 'residentTabLabel', label: 'Resident tab name', type: 'text' },
    { key: 'staffTabLabel', label: 'Staff tab name', type: 'text' },
];

export const COMPONENT_REGISTRY = {
    // ── Layout ───────────────────────────────────────────────────────
    section: {
        category: 'layout', label: 'Section', icon: 'fa-square', isContainer: true,
        defaultProps: {}, propFields: [LAYOUT_FIELD, STATUS_PANEL_FIELD, STATUS_INSET_FIELD],
    },
    container: {
        category: 'layout', label: 'Container', icon: 'fa-box', isContainer: true,
        defaultProps: {}, propFields: [LAYOUT_FIELD, STATUS_PANEL_FIELD, STATUS_INSET_FIELD],
    },
    row: {
        category: 'layout', label: 'Row', icon: 'fa-grip-lines', isContainer: true,
        defaultProps: {}, propFields: [],
    },
    column: {
        category: 'layout', label: 'Column', icon: 'fa-grip-lines-vertical', isContainer: true,
        defaultProps: {}, propFields: [],
    },
    grid: {
        category: 'layout', label: 'Grid', icon: 'fa-th', isContainer: true,
        defaultProps: {}, propFields: [],
    },
    spacer: {
        category: 'layout', label: 'Spacer', icon: 'fa-arrows-alt-v', isContainer: false,
        defaultProps: { height: '24px' },
        propFields: [{ key: 'height', label: 'Height', type: 'text' }],
    },
    divider: {
        category: 'layout', label: 'Divider', icon: 'fa-minus', isContainer: false,
        defaultProps: {}, propFields: [],
    },

    // ── Content ──────────────────────────────────────────────────────
    heading: {
        category: 'content', label: 'Heading', icon: 'fa-heading', isContainer: false,
        defaultProps: { text: 'Heading', level: 'h2' },
        propFields: [
            { key: 'text', label: 'Text', type: 'text' },
            { key: 'level', label: 'Level', type: 'select', options: ['h1', 'h2', 'h3', 'h4'] },
        ],
    },
    paragraph: {
        category: 'content', label: 'Paragraph', icon: 'fa-paragraph', isContainer: false,
        defaultProps: { text: 'Paragraph text' },
        propFields: [{ key: 'text', label: 'Text', type: 'textarea' }],
    },
    richtext: {
        category: 'content', label: 'Rich Text', icon: 'fa-align-left', isContainer: false,
        // Constrained content, not raw HTML (plan Decision #11): text plus an
        // optional link. Props can only hold plain strings/numbers/booleans —
        // the backend rejects arrays/objects — so this must stay flat.
        defaultProps: { text: 'Rich text', href: '' },
        propFields: [
            { key: 'text', label: 'Text', type: 'textarea' },
            { key: 'href', label: 'Link URL (optional)', type: 'text' },
        ],
    },
    image: {
        category: 'content', label: 'Image', icon: 'fa-image', isContainer: false,
        defaultProps: { src: '', alt: '' },
        propFields: [
            { key: 'src', label: 'Image', type: 'media' },
            { key: 'alt', label: 'Alt Text', type: 'text' },
        ],
    },
    logo: {
        category: 'content', label: 'Logo', icon: 'fa-award', isContainer: false,
        // Left empty, the company logo (Branding) is used; with no logo uploaded at all,
        // nameFallback shows the hotel name as a wordmark, with the tagline under it.
        defaultProps: { src: '' },
        propFields: [
            { key: 'src', label: 'Logo Image (empty = the company logo from Branding)', type: 'media' },
            { key: 'nameFallback', label: 'No logo anywhere? Show the hotel name', type: 'select', options: ['no', 'yes'] },
            { key: 'tagline', label: 'Line under the hotel name (only when the name is shown)', type: 'text' },
        ],
    },
    button: {
        category: 'content', label: 'Button', icon: 'fa-square-full', isContainer: false,
        defaultProps: { text: 'Button' },
        propFields: [{ key: 'text', label: 'Text', type: 'text' }],
    },
    icon: {
        category: 'content', label: 'Icon', icon: 'fa-star', isContainer: false,
        defaultProps: { icon: 'fa-star' },
        propFields: [{ key: 'icon', label: 'Icon class (e.g. fa-star)', type: 'text' }],
    },
    video: {
        category: 'content', label: 'Video', icon: 'fa-video', isContainer: false,
        defaultProps: { src: '' },
        propFields: [{ key: 'src', label: 'Video', type: 'media' }],
    },
    background: {
        category: 'content', label: 'Background', icon: 'fa-fill', isContainer: true,
        // The images live in node.layers (edited by BackgroundLayersEditor —
        // several stacked pictures, each aligned by mouse), not in a prop.
        // `src` is only the legacy single-image form older pages were saved
        // with; it is still rendered as one centred layer.
        defaultProps: { src: '' },
        propFields: [LAYOUT_FIELD],
    },

    // ── HotWifi smart components — fixed, non-extensible. Presentational
    // props only; the authRef.method is what actually selects behavior,
    // and is set once (not exposed as a freely-editable field) matching
    // how the component was placed. ──────────────────────────────────
    'otp-login': {
        category: 'hotwifi', label: 'OTP Login Form', icon: 'fa-mobile-alt', isContainer: false,
        authMethod: 'otp', defaultProps: {}, propFields: [LOOK_FIELD, ...FORM_LOOK_FIELDS],
    },
    'pms-login': {
        category: 'hotwifi', label: 'PMS Guest Login Form', icon: 'fa-hotel', isContainer: false,
        authMethod: 'pms', defaultProps: {}, propFields: [LOOK_FIELD, ...PMS_HINT_FIELDS, ...FORM_LOOK_FIELDS],
    },
    'voucher-login': {
        category: 'hotwifi', label: 'Voucher Login Form', icon: 'fa-ticket-alt', isContainer: false,
        authMethod: 'voucher', defaultProps: {}, propFields: [LOOK_FIELD, ...FORM_LOOK_FIELDS],
    },
    'resident-login': {
        category: 'hotwifi', label: 'Resident Login Form', icon: 'fa-key', isContainer: false,
        authMethod: 'resident', defaultProps: {}, propFields: [LOOK_FIELD, ...FORM_LOOK_FIELDS],
    },
    'staff-login': {
        category: 'hotwifi', label: 'Staff Login Form', icon: 'fa-id-badge', isContainer: false,
        authMethod: 'staff', defaultProps: {}, propFields: [LOOK_FIELD, ...FORM_LOOK_FIELDS],
    },
    // login-submit: the Terms checkbox + Connect button as a block you can
    // place anywhere — e.g. inside a login card — instead of at the very end
    // of the page. Without one, the page ends with the default pair.
    'login-submit': {
        category: 'hotwifi', label: 'Terms + Connect Button', icon: 'fa-check-square', isContainer: false,
        defaultProps: { text: 'Connect' },
        propFields: [
            LOOK_FIELD,
            { key: 'text', label: 'Button text', type: 'text' },
            { key: 'agreeText', label: 'Checkbox text (blank = I agree to the)', type: 'text' },
            { key: 'termsLinkText', label: 'Terms link text (blank = Terms and Conditions)', type: 'text' },
            // What the terms link opens — this page's own wording, over the branch's
            // Terms & Conditions (Branches → Edit). A whole Terms of Use fits: the API
            // takes this one prop long (landingPageSchema.js LONG_TEXT_PROPS).
            { key: 'termsTitle', label: 'Terms popup title (blank = Terms & Conditions)', type: 'text' },
            {
                key: 'termsText', label: 'Terms popup text (blank = the branch\'s Terms & Conditions)', type: 'textarea',
                rows: 10, maxWords: 10000, placeholder: 'Type or paste the terms guests read when they tap the terms link…',
            },
            colorField('labelColor', 'Terms text colour'),
            colorField('linkColor', 'Terms link colour'),
        ],
    },
    // login-methods: the whole login box in one block — a tab per method the
    // branch has enabled, and the form for the selected tab, like the classic
    // portal page. Drop this in to support every login method without
    // placing each form by hand. (Keep the entry's first line below in the
    // standard format — registryParity.test.js reads it.)
    'login-methods': {
        category: 'hotwifi', label: 'All Login Methods', icon: 'fa-sign-in-alt', isContainer: false,
        defaultProps: {},
        propFields: [
            LOOK_FIELD,
            // no: the first method's form only, as the Oakwood page — a Free Wi-Fi Access
            // Button (with its back text) then switches to the access code and back.
            { key: 'showTabs', label: 'Method tabs (no = the first method only; switch with an access code button)', type: 'select', options: ['yes', 'no'] },
            ...TAB_LABEL_FIELDS, ...PMS_HINT_FIELDS, ...TAB_LOOK_FIELDS, ...FORM_LOOK_FIELDS,
        ],
    },
    'auth-method-selector': {
        category: 'hotwifi', label: 'Authentication Method Selector', icon: 'fa-list', isContainer: false,
        defaultProps: {}, propFields: [],
    },
    'free-access-button': {
        category: 'hotwifi', label: 'Free Wi-Fi Access Button', icon: 'fa-wifi', isContainer: false,
        defaultProps: { text: 'Continue with Free Wi-Fi' },
        propFields: [
            { key: 'text', label: 'Button Text', type: 'text' },
            { key: 'backText', label: 'Back text, shown on the access code form to go back (blank = none)', type: 'text' },
        ],
    },
    'terms-conditions': {
        category: 'hotwifi', label: 'Terms & Conditions', icon: 'fa-file-contract', isContainer: false,
        // props.text is a fallback only — the renderer prefers the
        // branch's real, admin-configured termsAndConditions whenever set
        // (see LandingPageRenderer.jsx), so operational T&C text isn't
        // duplicated/out-of-sync between the two systems.
        defaultProps: { text: '' },
        propFields: [{ key: 'text', label: 'Fallback text (used only if the branch has no Terms configured)', type: 'textarea' }],
    },
    'privacy-policy': {
        category: 'hotwifi', label: 'Privacy Policy', icon: 'fa-user-shield', isContainer: false,
        defaultProps: { text: '' },
        propFields: [{ key: 'text', label: 'Text', type: 'textarea' }],
    },
    'hotel-info': {
        category: 'hotwifi', label: 'Hotel Information', icon: 'fa-info-circle', isContainer: false,
        defaultProps: {}, propFields: [],
    },
    'guest-welcome': {
        category: 'hotwifi', label: 'Guest Welcome Section', icon: 'fa-hand-sparkles', isContainer: false,
        defaultProps: {}, propFields: [],
    },
    'qr-code': {
        category: 'hotwifi', label: 'QR Code', icon: 'fa-qrcode', isContainer: false,
        defaultProps: { size: 128 },
        propFields: [
            { key: 'value', label: 'QR Value (blank = portal URL)', type: 'text' },
            { key: 'size', label: 'Size (px)', type: 'number' },
        ],
    },
    'contact-info': {
        category: 'hotwifi', label: 'Contact Information', icon: 'fa-address-card', isContainer: false,
        defaultProps: { text: '' },
        propFields: [{ key: 'text', label: 'Text', type: 'textarea' }],
    },
    // scenic-portal: a whole sign-in page in one block — photo, organisation logo and
    // name, a big place name, EN / हिं / मरा, and a card that signs the guest in with a
    // WhatsApp or SMS one-time code (for an ISP: then the plan and payment). See
    // Portal/scenic/ScenicPortal.jsx. Left empty, the logo and photo are the company's
    // own (Branches / Tenant Profile → Branding), so each organisation gets its own.
    'scenic-portal': {
        category: 'hotwifi', label: 'Scenic Sign-in Page (WhatsApp / SMS OTP)', icon: 'fa-mountain', isContainer: false,
        defaultProps: {
            logo: '', background: '', heroTitle: '', orgNameEn: '', orgNameHi: '', orgNameMr: '',
            defaultLanguage: 'en', showLanguages: 'yes', showTrack: 'yes', poweredBy: 'AsyncPulse Technologies',
        },
        propFields: [
            { key: 'logo', label: 'Organisation logo (empty = the company logo from Branding)', type: 'media' },
            { key: 'background', label: 'Background photo (empty = the Portal Background from Branding)', type: 'media' },
            { key: 'orgNameEn', label: 'Organisation name, English (empty = the company name)', type: 'text' },
            { key: 'orgNameHi', label: 'Organisation name, हिंदी', type: 'text' },
            { key: 'orgNameMr', label: 'Organisation name, मराठी', type: 'text' },
            { key: 'heroTitle', label: 'Big name on the photo (empty = the branch name)', type: 'text' },
            { key: 'taglineEn', label: 'Line under it, English (empty = Free public Wi-Fi)', type: 'text' },
            { key: 'taglineHi', label: 'Line under it, हिंदी', type: 'text' },
            { key: 'taglineMr', label: 'Line under it, मराठी', type: 'text' },
            colorField('brandColor', 'Buttons and headings colour (empty = the company colour)'),
            { key: 'freeSessionMinutes', label: 'Free minutes per session, said under the button (0 = from the plans)', type: 'number' },
            { key: 'poweredBy', label: 'Developed by (company name, empty = no credit line)', type: 'text' },
            { key: 'defaultLanguage', label: 'First language', type: 'select', options: ['en', 'hi', 'mr'] },
            { key: 'showLanguages', label: 'Show the EN / हिं / मरा switch', type: 'select', options: ['yes', 'no'] },
            { key: 'showTrack', label: 'Show the toy-train progress track', type: 'select', options: ['yes', 'no'] },
        ],
    },
};

// Auth-smart types that render ONE specific method and need an authRef —
// kept in one place so the builder can stamp it automatically when a
// smart component is added. Matches backend/utils/landingPageSchema.js's
// AUTH_SMART_TYPES exactly (auth-method-selector is deliberately excluded
// — it shows all configured methods as tabs, not one specific method).
export const AUTH_SMART_TYPES = ['pms-login', 'voucher-login', 'otp-login', 'resident-login', 'staff-login'];

/**
 * Builds a brand-new node of the given type with a fresh id and the
 * registry's default props — used by the palette's "click to add" (Phase
 * 5) and later drag-drop (Phase 6) flows.
 */
export function createNode(type) {
    const def = COMPONENT_REGISTRY[type];
    if (!def) throw new Error(`Unknown component type: ${type}`);
    const node = {
        id: generateNodeId(),
        type,
        props: { ...def.defaultProps },
    };
    if (def.isContainer) node.children = [];
    if (AUTH_SMART_TYPES.includes(type) && def.authMethod) {
        node.authRef = { method: def.authMethod };
    }
    return node;
}

export function isContainerType(type) {
    return !!COMPONENT_REGISTRY[type]?.isContainer;
}
